import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { packages } from "@/mocks/products";

export default function PackagesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll(".reveal");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handlePackageClick = (index: number, id: string) => {
    const card = cardRefs.current[index];
    if (card) {
      card.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setHighlightedId(id);
    setTimeout(() => setHighlightedId(null), 2500);
  };

  return (
    <section ref={sectionRef} className="bg-dark-bg py-24 md:py-32">
      <div className="w-full px-6 md:px-10 lg:px-16">
        {/* Header Row - Package Names as the Title */}
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-8 mb-16 md:mb-20">
          <div className="reveal opacity-0 translate-y-4 transition-all duration-600 ease-out">
            <p className="text-xs font-sans font-semibold tracking-[0.2em] uppercase text-gold mb-4">
              Our collection
            </p>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              {packages.map((pkg, index) => (
                <span key={pkg.id} className="flex items-baseline gap-3">
                  <Link
                    to={`/packages#${pkg.id}`}
                    className="font-serif text-3xl md:text-4xl lg:text-5xl italic text-charcoal-50 leading-tight hover:text-gold transition-colors duration-300 select-none"
                  >
                    {pkg.name}
                  </Link>
                  {index < packages.length - 1 && (
                    <span className="text-gold/40 text-2xl md:text-3xl font-serif italic select-none">
                      /
                    </span>
                  )}
                </span>
              ))}
            </div>
          </div>
          <div className="reveal opacity-0 translate-y-4 transition-all duration-600 ease-out delay-150 lg:max-w-sm lg:pb-1">
            <p className="text-base text-charcoal-300 font-sans leading-relaxed">
              Four tiers of protection, each designed for different lifestyles
              and water quality needs.
            </p>
          </div>
        </div>

        {/* Card Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {packages.map((pkg, index) => (
            <div
              key={pkg.id}
              ref={(el) => { cardRefs.current[index] = el; }}
              className={`reveal opacity-0 translate-y-6 transition-all duration-700 ease-out group cursor-pointer`}
              style={{ transitionDelay: `${index * 120}ms` }}
              onMouseEnter={() => setHoveredId(pkg.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <Link to={`/packages#${pkg.id}`} className="block">
                {/* Card */}
                <div
                  className={`relative rounded-2xl overflow-hidden bg-dark-surface transition-all duration-500 hover:border-gold/40 ${
                    highlightedId === pkg.id
                      ? "border-gold shadow-[0_0_30px_rgba(201,168,76,0.15)] scale-[1.02]"
                      : "border border-dark-border"
                  }`}
                >
                  {/* Image */}
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img
                      src={pkg.image}
                      alt={pkg.name}
                      className={`w-full h-full object-cover object-top transition-transform duration-700 ${
                        hoveredId === pkg.id ? "scale-105" : "scale-100"
                      }`}
                      loading="lazy"
                    />
                    <div
                      className={`absolute inset-0 bg-dark-bg/20 transition-opacity duration-500 ${
                        hoveredId === pkg.id ? "opacity-0" : "opacity-100"
                      }`}
                    />
                  </div>

                  {/* Content */}
                  <div className="p-5 md:p-6">
                    <span className="inline-block px-3 py-1 bg-gold/10 text-gold text-xs font-sans font-semibold rounded-full mb-3 border border-gold/20">
                      {pkg.tier}
                    </span>
                    <h3 className="font-serif text-xl md:text-2xl font-semibold text-charcoal-50 leading-tight mb-4">
                      {pkg.name}
                    </h3>
                    <div className="flex items-center justify-between">
                      {pkg.id === "diamond" ? (
                        <span className="font-serif text-2xl font-semibold text-gold">
                          Inquire
                        </span>
                      ) : (
                        <span className="font-serif text-2xl font-semibold text-gold">
                          ${pkg.price.toLocaleString()}
                        </span>
                      )}
                      <span
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                          hoveredId === pkg.id
                            ? "bg-gold text-dark-bg"
                            : "bg-charcoal-700 text-gold"
                        }`}
                      >
                        <i className="ri-arrow-right-line text-sm"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}